import type { FlowColumnDef, FlowSortOption } from '../types';

/**
 * Pure column-sort helpers shared by `FlowTable` and the V2 `LedgerTable`
 * (plan 2026-09-27-007 D5/U1). Glaze-free and React-free by design.
 */

export function defaultDirFor(type: FlowColumnDef<never>['type']): 'ASC' | 'DESC' {
  return type === 'number' || type === 'date' || type === 'boolean' ? 'DESC' : 'ASC';
}

/**
 * Whether a header click sorts this column (round-1 fix R11 / D-G). ONE
 * predicate, read by BOTH `headerClick` and the header's cursor/hover class
 * — they disagreed once already and a header that looks clickable but isn't
 * (or vice versa) is the visible half of that bug.
 *
 * `noSort` is the absolute veto. Otherwise a `noFilter` column sorts only
 * when it explicitly opts in via `sortable` — see `FlowColumnDef.sortable`
 * for why filterability stopped being a proxy for sortability.
 */
export function isSortableColumn(col: FlowColumnDef<never>): boolean {
  if (col.noSort) return false;
  return !col.noFilter || col.sortable === true;
}

/**
 * Sort choices for the V2 `SortPicker` (plan 2026-09-28-003 D4, scope add).
 * Every mount today has a Sort control — a bare `config.sortOptions` array
 * (Signals-style explicit `(key, dir)` pairs) is returned verbatim; when the
 * mount declares none (Companies/People today), one entry is derived per
 * `isSortableColumn` column, using that column's own `defaultDirFor` as the
 * option's `dir` — so picking a NEW field always resets to that column's
 * natural first-click direction rather than carrying over whatever the
 * previous field's direction happened to be.
 */
export function sortOptionsOf(config: {
  columns: FlowColumnDef<never>[];
  sortOptions?: FlowSortOption[];
}): FlowSortOption[] {
  if (config.sortOptions) return config.sortOptions;
  return config.columns
    .filter(isSortableColumn)
    .map((c) => ({ key: c.key, label: c.label, dir: defaultDirFor(c.type) }));
}

/**
 * NEVER BLANK trigger text (V1 `SortPicker`'s rule, ported verbatim): the
 * working sort only reads as one of `options` when BOTH `key` AND `dir`
 * match exactly — a header click that lands on a field/direction pair not
 * among the configured options (e.g. a plain column click when
 * `config.sortOptions` is Signals-style) falls back to `"<column label>
 * <arrow>"` from `columns`, and finally to the raw sort field itself if even
 * that catalog has no match — so the trigger always says something
 * meaningful about the CURRENT sort, never an option label that doesn't
 * describe it.
 */
export function sortTriggerLabel(
  sort: { field: string; dir: 'ASC' | 'DESC' },
  options: FlowSortOption[],
  columns: Array<{ key: string; label: string }>,
): string {
  const current = options.find((o) => o.key === sort.field && o.dir === sort.dir);
  if (current) return current.label;
  const arrow = sort.dir === 'DESC' ? '↓' : '↑';
  const fallbackLabel = columns.find((c) => c.key === sort.field)?.label ?? sort.field;
  return `${fallbackLabel} ${arrow}`;
}
